// Adds SEO depth to the city story sections: a short extra paragraph plus a
// scannable checklist for each block. Written as templates so every city page
// reads locally without hand-authoring 80+ blocks.

type Extra = { extra: string; bullets: string[]; link: { slug: string; label: string } };

const LINKS: { slug: string; label: string }[] = [
  { slug: "roof-repair", label: "Explore Roof Repair" },
  { slug: "roof-replacement", label: "Explore Roof Replacement" },
  { slug: "solar-installation", label: "Explore Solar Installation" },
  { slug: "residential-roofing", label: "Explore Residential Roofing" },
  { slug: "roof-inspection", label: "Explore Roof Inspection" },
  { slug: "solar-battery-installation", label: "Explore Battery Storage" },
];

type ExtraBase = { extra: string; bullets: string[] };

const TEMPLATES: ((city: string) => ExtraBase)[] = [
  (city) => ({
    extra: `Most calls we get in ${city} start the same way: a stain on the ceiling, a few loose pieces after a windy week, or a repair from years back that finally gave out. We walk the roof, photograph what we find and show you the pictures before we quote anything, so you can see the actual condition instead of taking our word for it.`,
    bullets: [
      "Free roof inspection with photos of every problem area",
      "Leak tracing from the interior stain back to the real entry point",
      "Flashing, valley and penetration checks around vents and skylights",
      "Honest note on what can wait another season and what can't",
    ],
  }),
  (city) => ({
    extra: `When we do recommend a full replacement in ${city}, you get a written scope: tear-off, decking repairs if we find soft spots, new underlayment, ventilation and the exact material line we're installing. No vague allowances that grow halfway through the job.`,
    bullets: [
      "Repair-vs-replace numbers side by side before you decide",
      "Tear-off, deck inspection and dry-in handled by our own crew",
      "Ventilation corrected so the new roof lasts its full life",
      "Manufacturer and workmanship warranties in writing",
    ],
  }),
  (city) => ({
    extra: `On the solar side we design around your last 12 months of usage in ${city}, not a generic package. If the roof underneath has only a few years left, we say so up front and sequence the roofing work first so you never pay to remove an array twice.`,
    bullets: [
      "System sized from your real utility usage history",
      "Roof condition checked before any array is designed",
      "Battery, inverter and EV charger options explained plainly",
      "Roofing and solar handled by one company, one warranty conversation",
    ],
  }),
  (city) => ({
    extra: `Being local to ${city} is not a marketing line for us. Our crews are on these streets weekly, which is why follow-up visits, warranty questions and small punch-list items don't turn into a month of phone tag.`,
    bullets: [
      "Same-day response on most quote requests",
      "Arrival inside the window we promised",
      "Licensed, insured crews – no rotating subcontractors",
      "Local references in and around " + city + " on request",
    ],
  }),
  (city) => ({
    extra: `Paperwork is part of the job in ${city}, not an afterthought. We pull the permits, meet the inspector and hand you the closed-out documentation, which matters later when you refinance or sell.`,
    bullets: [
      "Permits pulled before the first shingle comes off",
      "Inspections scheduled and attended by our team",
      "Code-compliant ventilation, fastening and underlayment",
      "Final documentation kept on file for your records",
    ],
  }),
  (city) => ({
    extra: `Cost is usually the real question. We put clear numbers in front of ${city} homeowners – what the work is, what it isn't, and what financing looks like if you'd rather spread it out.`,
    bullets: [
      "Itemized estimates with no surprise change orders",
      "Financing options for roofing and solar projects",
      "Payback math shown with your own utility numbers",
      "Free second opinion if you already have a bid in hand",
    ],
  }),
];

export function getCitySectionExtra(city: string, index: number): Extra {
  const i = index % TEMPLATES.length;
  return { ...TEMPLATES[i](city), link: LINKS[i] };
}
